import { Dialog } from '@headlessui/react';
import WebUtil from '@payaca/shared-web/utilities/index';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx, clsx } from '@payaca/utilities/cvaMerge';
import { FC, PropsWithChildren, useId, useMemo } from 'react';
import Button from '../Button/Button';
import ModalCore, { ModalCoreProps } from './ModalCore';

export interface ModalProps extends ModalCoreProps {
  title: string;
  onBack?: () => void;
}

const Modal: FC<PropsWithChildren<ModalProps>> = (props) => {
  const {
    title,
    draggable = true,
    className,
    children,
    onBack,
    onClose,
    size = 'sm',
    ...rest
  } = props;

  const uId = useId().replace(/:/g, '');

  const isDraggable = useMemo(
    () => (WebUtil.isMobileScreen() ? false : draggable),
    [draggable]
  );

  return (
    <ModalCore
      uId={uId}
      draggable={isDraggable}
      className={clsx(
        `flex max-h-full flex-col overflow-hidden rounded-xl border bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-gray-700/[.7]`,
        className
      )}
      onClose={onClose}
      size={size}
      {...rest}
    >
      {/*Header*/}
      <div
        className={clsx(
          `payaca-modal-handle${uId}`,
          `flex flex-col border-b px-4 py-3 dark:border-gray-700`,
          isDraggable && 'cursor-move'
        )}
      >
        {isDraggable && (
          <div className="mx-auto mb-1 h-[4px] w-8 rounded bg-gray-400/80">
            <span className="sr-only">Drag Modal</span>
          </div>
        )}

        <div className="typography:prose flex items-center gap-2">
          {onBack && (
            <Button
              variant={'ghost'}
              colour={'grey'}
              size={'xs'}
              onClick={onBack}
            >
              <span className="sr-only">Back</span>
              <UntitledIcon className="h-5 w-5" name="chevron-left" />
            </Button>
          )}
          <Dialog.Title>{title}</Dialog.Title>
          {onClose && (
            <Button
              className="ml-auto"
              variant={'ghost'}
              colour={'grey'}
              size={'sm'}
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <UntitledIcon className="h-3 w-3" name="x-close" />
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-y-auto">{children}</div>
    </ModalCore>
  );
};

const ModalBody: FC<PropsWithChildren<{ className?: string }>> = (props) => {
  const { className, children } = props;

  return (
    <div className={clstx('typography:prose p-4', className)}>{children}</div>
  );
};

const ModalFooter: FC<PropsWithChildren<{ className?: string }>> = (props) => {
  const { className, children } = props;

  return (
    <div
      className={clstx(
        'z-sticky sticky bottom-0 border-t bg-white px-4 py-3 dark:border-gray-700',
        className
      )}
    >
      {children}
    </div>
  );
};

const ModalFooterActions: FC<PropsWithChildren<{ className?: string }>> = (
  props
) => {
  const { className, children } = props;

  return (
    <div className={clstx('flex items-center justify-end gap-x-2', className)}>
      {children}
    </div>
  );
};

export default Object.assign(Modal, {
  Body: ModalBody,
  Footer: Object.assign(ModalFooter, { Actions: ModalFooterActions }),
});
